<script setup lang="ts">
import type { LanguageSwitchGroup, LanguageSwitchOption, VLanguageSwitchProps } from "./VLanguageSwitch.types";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import VDropdown from "../Dropdown/VDropdown.vue";
import VDropdownItem from "../DropdownItem/VDropdownItem.vue";
import VIcon from "../Icon/VIcon.vue";
import { FLAG_IMAGE_CLASS } from "../LocaleSelect/locale";
import VTag from "../Tag/VTag.vue";

defineOptions({
  name: "VLanguageSwitch",
});

const {
  languages,
  groups = [],
  label = undefined,
  disabled = false,
  variant = "full",
  placement = "top-start",
} = defineProps<VLanguageSwitchProps>();

const model = defineModel<string>({ default: "" });

const { t } = useI18n();

const resolvedLabel = computed(() => label ?? t("base.language.placeholder"));
const canSwitch = computed(() => languages.length > 1);
const current = computed<LanguageSwitchOption | null>(() =>
  languages.find(language => language.value === model.value) ?? languages[0] ?? null,
);
const compact = computed(() => variant === "compact");
const field = computed(() => variant === "field");
// The compact pill shows the locale code; an option without a flag (an original of unknown language) has none,
// so its globe stands alone and the name stays in the title.
const pillText = computed(() => {
  if (!compact.value) {
    return current.value?.label ?? "";
  }
  return current.value?.flag ? current.value.value : "";
});
const attention = computed(() => current.value?.tag?.variant === "is-warning");

// The field variant has the look of a select: a 32px pill with an input border, 14px text and the caret at the end.
const pillClasses = computed(() => {
  if (field.value) {
    return [
      "inline-flex h-8 min-w-0 items-center gap-2.5 rounded-full border border-solid border-gray-300 bg-white pl-4 pr-4 text-sm leading-none transition-colors text-gray-900 dark:border-dark-600 dark:bg-dark-800 dark:text-gray-100",
    ];
  }
  return [
    "inline-flex min-w-0 items-center rounded-full font-medium leading-none transition-colors text-gray-700 dark:text-gray-200",
    compact.value ? "h-6 gap-1 px-2 text-[11px] uppercase tracking-wide" : "h-6 gap-1.5 px-2 text-xs",
  ];
});
const hoverClass = computed(() => (field.value
  ? "focus-visible:outline-hidden focus-visible:border-primary-500 hover:border-gray-400 dark:hover:border-dark-500"
  : "hover:bg-gray-100 dark:hover:bg-dark-700"));
const caretClass = computed(() => (field.value
  ? "ml-auto pl-1.5 text-[10px] text-gray-800 dark:text-gray-300"
  : "text-[10px] text-gray-500 dark:text-gray-400"));

interface OptionSection {
  group: LanguageSwitchGroup | null
  options: LanguageSwitchOption[]
}

const sections = computed<OptionSection[]>(() => {
  const knownGroups = new Set(groups.map(group => group.key));
  const ungrouped = languages.filter(language => !language.group || !knownGroups.has(language.group));
  const result: OptionSection[] = [{ group: null, options: ungrouped }];
  for (const group of groups) {
    result.push({ group, options: languages.filter(language => language.group === group.key) });
  }
  return result.filter(section => section.options.length > 0);
});

/** Groups opened or closed by hand; the rest start open only while they hold the current language. */
const groupState = ref<Record<string, boolean>>({});

function isOpen(section: OptionSection): boolean {
  if (!section.group?.collapsible) {
    return true;
  }
  const chosen = groupState.value[section.group.key];
  if (chosen !== undefined) {
    return chosen;
  }
  return section.options.some(language => language.value === current.value?.value);
}

function toggle(section: OptionSection) {
  if (!section.group) {
    return;
  }
  groupState.value = { ...groupState.value, [section.group.key]: !isOpen(section) };
}

function select(language: LanguageSwitchOption) {
  if (language.disabled || language.value === model.value) {
    return;
  }
  model.value = language.value;
}

function flagSrc(flag: string) {
  return `/flags/${flag.toUpperCase()}.svg`;
}
</script>

<template>
  <template v-if="current">
    <VDropdown
      v-if="canSwitch"
      theme="verbleif-popper"
      popper-class="v-scroll-menu-popper"
      :placement="placement"
      :disabled="disabled"
    >
      <button
        type="button"
        :class="[pillClasses, disabled ? 'cursor-not-allowed' : hoverClass]"
        :disabled="disabled"
        :aria-label="resolvedLabel"
        :title="compact ? current.label : undefined"
        aria-haspopup="true"
        data-test="language-pill"
      >
        <img
          v-if="current.flag"
          :class="FLAG_IMAGE_CLASS"
          :src="flagSrc(current.flag)"
          alt=""
        >
        <VIcon v-else name="globe" class="text-gray-500 dark:text-gray-400" />
        <span v-if="pillText" class="truncate leading-none">{{ pillText }}</span>
        <span
          v-if="compact && attention"
          class="h-1.5 w-1.5 shrink-0 rounded-full bg-warning-500"
          data-test="language-attention"
        />
        <VTag
          v-if="!compact && current.tag"
          :text="current.tag.text"
          :variant="current.tag.variant"
          size="extra-small"
        />
        <VIcon
          name="chevron-down"
          :class="caretClass"
        />
      </button>
      <template #popper="{ hide }">
        <div class="options v-scroll-menu" data-test="language-options">
          <template v-for="section in sections" :key="section.group?.key ?? ''">
            <button
              v-if="section.group?.collapsible"
              type="button"
              class="flex w-full items-center justify-between gap-2 px-2 pb-1 pt-2 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              :aria-expanded="isOpen(section)"
              :data-group="section.group.key"
              data-test="language-group"
              @click="toggle(section)"
            >
              <span>{{ section.group.label }} ({{ section.options.length }})</span>
              <VIcon :name="(isOpen(section) ? 'chevron-up' : 'chevron-down')" class="text-[10px]" />
            </button>
            <div
              v-else-if="section.group"
              class="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
              :data-group="section.group.key"
              data-test="language-group"
            >
              {{ section.group.label }}
            </div>
            <template v-if="isOpen(section)">
              <VDropdownItem
                v-for="language in section.options"
                :key="language.value"
                :text="language.label"
                :secondary-text="language.secondary ?? null"
                :flag="language.flag ?? null"
                :icon="language.flag ? null : 'globe'"
                :disabled="language.disabled"
                :disabled-notice="language.tooltip"
                size="small"
                :hide="hide"
                :class="{ 'is-primary': language.value === current.value }"
                :data-value="language.value"
                @click="select(language)"
              >
                <template #append>
                  <VTag
                    v-if="language.tag"
                    :text="language.tag.text"
                    :variant="language.tag.variant"
                    size="extra-small"
                  />
                  <VIcon
                    v-if="language.value === current.value"
                    name="check"
                    class="text-xs"
                  />
                </template>
              </VDropdownItem>
            </template>
          </template>
          <slot name="footer" :hide="hide" />
        </div>
      </template>
    </VDropdown>
    <span
      v-else
      :class="pillClasses"
      :title="compact ? current.label : undefined"
      data-test="language-pill"
    >
      <img
        v-if="current.flag"
        :class="FLAG_IMAGE_CLASS"
        :src="flagSrc(current.flag)"
        alt=""
      >
      <VIcon v-else name="globe" class="text-gray-500 dark:text-gray-400" />
      <span v-if="pillText" class="truncate leading-none">{{ pillText }}</span>
      <VTag
        v-if="!compact && current.tag"
        :text="current.tag.text"
        :variant="current.tag.variant"
        size="extra-small"
      />
    </span>
  </template>
</template>
